import type { WeaponKind } from "../weapons/Weapon";
import { PALETTE } from "./palettes";

export class WeaponSpriteGenerator {
  private readonly cache = new Map<WeaponKind, HTMLCanvasElement>();

  public get(weapon: WeaponKind): HTMLCanvasElement {
    const cached = this.cache.get(weapon);
    if (cached) {
      return cached;
    }
    const canvas = document.createElement("canvas");
    canvas.width = 160;
    canvas.height = 110;
    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Canvas 2D context is unavailable");
    }
    context.imageSmoothingEnabled = false;
    if (weapon === "pistol") {
      this.drawPistol(context);
    } else if (weapon === "rifle") {
      this.drawRifle(context);
    } else {
      this.drawShotgun(context);
    }
    this.cache.set(weapon, canvas);
    return canvas;
  }

  private drawPistol(context: CanvasRenderingContext2D): void {
    context.fillStyle = "rgba(0,0,0,.42)";
    this.fillPolygon(context, [52, 110, 108, 110, 104, 55, 96, 43, 64, 43, 56, 55]);
    context.fillStyle = PALETTE.darkRust;
    this.fillPolygon(context, [63, 110, 97, 110, 94, 67, 66, 67]);
    context.fillStyle = PALETTE.rust;
    this.fillPolygon(context, [70, 105, 90, 105, 88, 70, 72, 70]);
    context.fillStyle = PALETTE.iron;
    this.fillPolygon(context, [54, 67, 106, 67, 98, 43, 62, 43]);
    context.fillStyle = PALETTE.steel;
    this.fillPolygon(context, [61, 48, 99, 48, 92, 18, 68, 18]);
    context.fillStyle = PALETTE.paleSteel;
    this.fillPolygon(context, [68, 43, 92, 43, 87, 24, 73, 24]);
    context.fillStyle = PALETTE.black;
    context.fillRect(68, 14, 24, 12);
    context.fillRect(74, 17, 12, 7);
    context.fillStyle = PALETTE.cyan;
    context.fillRect(77, 29, 6, 3);
    context.fillStyle = PALETTE.bone;
    context.fillRect(53, 83, 13, 27);
    context.fillRect(94, 83, 13, 27);
  }

  private drawRifle(context: CanvasRenderingContext2D): void {
    context.fillStyle = "#171c1a";
    this.fillPolygon(context, [31,110,129,110,108,63,93,47,67,47,52,63]);
    context.fillStyle = "#626b60";
    this.fillPolygon(context,[51,99,108,99,101,54,89,42,71,42,59,54]);
    context.fillStyle = "#28362b";
    context.fillRect(58,66,44,29);
    context.fillStyle = "#9ca18b";
    this.fillPolygon(context,[69,59,91,59,86,17,74,17]);
    context.fillStyle = "#303933";
    context.fillRect(73,10,14,39);
    context.fillStyle = "#aab0a0";
    context.fillRect(76,13,3,34);
    context.fillStyle = "#111714";
    context.fillRect(70,8,20,9);
    context.fillRect(77,11,6,5);
    for(let y=51;y<85;y+=6) {context.fillRect(60,y,10,3);context.fillRect(91,y,10,3);}
    context.fillStyle = "#daac53";
    context.fillRect(77,41,6,3);
    context.fillStyle = "#7e674a";
    this.fillPolygon(context,[33,110,57,110,61,83,49,78]);
    this.fillPolygon(context,[105,110,125,110,112,78,101,83]);
    context.fillStyle = "#b2ba9e";
    context.fillRect(75,69,10,2);
  }

  private drawShotgun(context: CanvasRenderingContext2D): void {
    context.fillStyle = "rgba(0,0,0,.45)";
    this.fillPolygon(context, [38, 110, 122, 110, 108, 60, 98, 49, 62, 49, 52, 60]);
    context.fillStyle = PALETTE.bone;
    this.fillPolygon(context, [37, 110, 61, 110, 67, 76, 51, 72]);
    this.fillPolygon(context, [99, 110, 123, 110, 109, 72, 93, 76]);
    context.fillStyle = PALETTE.darkRust;
    this.fillPolygon(context, [63, 110, 97, 110, 94, 74, 66, 74]);
    context.fillStyle = PALETTE.rust;
    this.fillPolygon(context, [55, 76, 105, 76, 100, 55, 60, 55]);
    for (let x = 63; x <= 95; x += 8) context.fillRect(x, 59, 4, 14);
    context.fillStyle = PALETTE.iron;
    this.fillPolygon(context, [50, 59, 110, 59, 101, 43, 59, 43]);
    context.fillStyle = PALETTE.steel;
    this.fillPolygon(context, [60, 47, 100, 47, 92, 16, 68, 16]);
    context.fillStyle = PALETTE.paleSteel;
    this.fillPolygon(context, [68, 42, 92, 42, 87, 22, 73, 22]);
    context.fillStyle = PALETTE.black;
    context.fillRect(66, 10, 28, 13);
    context.fillRect(71, 13, 18, 8);
    context.fillStyle = PALETTE.amber;
    context.fillRect(77, 30, 6, 4);
  }

  private fillPolygon(context: CanvasRenderingContext2D, coordinates: number[]): void {
    const [firstX, firstY, ...remaining] = coordinates;
    if (firstX === undefined || firstY === undefined) return;
    context.beginPath();
    context.moveTo(firstX, firstY);
    for (let index = 0; index < remaining.length; index += 2) {
      const x = remaining[index];
      const y = remaining[index + 1];
      if (x !== undefined && y !== undefined) context.lineTo(x, y);
    }
    context.closePath();
    context.fill();
  }
}
